// ---------- RULE OBJECT

import {
  EAllowStatus,
  ICountryGroup,
  TargetingCCPA,
  TargetingCoppa,
  TargetingLogic,
} from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import { IPillItem } from '~/src/dash/components/pill-switcher';

// Active enum
export const BrandSafetyRuleActiveUnknown: number = 0;
export const BrandSafetyRuleActiveInactive: number = 1;
export const BrandSafetyRuleActiveActive: number = 2;

// Targeting type enum
export const BrandSafetyRuleTargetingTypeUnknown: number = 0;
export const BrandSafetyRuleTargetingTypeGlobal: number = 1;
export const BrandSafetyRuleTargetingTypeBidder: number = 2;
export const BrandSafetyRuleTargetingTypeBidderGroup: number = 3;
export const BrandSafetyRuleTargetingTypeChannel: number = 4;
export const BrandSafetyRuleTargetingTypeChannelGroup: number = 5;

export const BrandSafetyRuleTargetingTypeUnknownString: string = 'Unknown';
export const BrandSafetyRuleTargetingTypeBidderString: string = 'Bidder';
export const BrandSafetyRuleTargetingTypeBidderGroupString: string = 'Bidder Group';
export const BrandSafetyRuleTargetingTypeChannelString: string = 'Channel';
export const BrandSafetyRuleTargetingTypeChannelGroupString: string = 'Channel Group';

export type BrandSafetyRuleTargetingAdomainExceptionState = number;
export const BrandSafetyRuleTargetingAdomainExceptionStateUnknown: BrandSafetyRuleTargetingAdomainExceptionState = 0;
export const BrandSafetyRuleTargetingAdomainExceptionStateExactMatch: BrandSafetyRuleTargetingAdomainExceptionState = 1;
export const BrandSafetyRuleTargetingAdomainExceptionStateContains: BrandSafetyRuleTargetingAdomainExceptionState = 2;
export const BrandSafetyRuleTargetingAdomainExceptionStateAllowAll: BrandSafetyRuleTargetingAdomainExceptionState = 3;

export function brandSafetyTargetingTypeToString(brandSafetyRuleTargetingType: number): string {
  switch (brandSafetyRuleTargetingType) {
    case BrandSafetyRuleTargetingTypeBidder:
      return BrandSafetyRuleTargetingTypeBidderString;
    case BrandSafetyRuleTargetingTypeBidderGroup:
      return BrandSafetyRuleTargetingTypeBidderGroupString;
    case BrandSafetyRuleTargetingTypeChannel:
      return BrandSafetyRuleTargetingTypeChannelString;
    case BrandSafetyRuleTargetingTypeChannelGroup:
      return BrandSafetyRuleTargetingTypeChannelGroupString;
    default:
      break;
  }
  return BrandSafetyRuleTargetingTypeUnknownString;
}

export function brandSafetyTargetingTypeToNumber(brandSafetyRuleTargetingType: string): number {
  switch (brandSafetyRuleTargetingType) {
    case BrandSafetyRuleTargetingTypeBidderString:
      return BrandSafetyRuleTargetingTypeBidder;
    case BrandSafetyRuleTargetingTypeBidderGroupString:
      return BrandSafetyRuleTargetingTypeBidderGroup;
    case BrandSafetyRuleTargetingTypeChannelString:
      return BrandSafetyRuleTargetingTypeChannel;
    case BrandSafetyRuleTargetingTypeChannelGroupString:
      return BrandSafetyRuleTargetingTypeChannelGroup;
    default:
      break;
  }
  return BrandSafetyRuleTargetingTypeUnknown;
}

export const BRAND_SAFETY_TARGETING_PILLS: IPillItem[] = [
  { text: BrandSafetyRuleTargetingTypeChannelString },
  { text: BrandSafetyRuleTargetingTypeChannelGroupString },
  { text: BrandSafetyRuleTargetingTypeBidderString },
  { text: BrandSafetyRuleTargetingTypeBidderGroupString },
];

// enum for rule type (IAB, Adomain...)
export const RuleTypeIAB = 1;
export const RuleTypeAdomain = 2;
export const RuleTypeMediafile = 3;
export const RuleTypeLanguage = 4;
export const RuleTypeCreativeID = 5;
export const RuleTypeEventTracker = 6;

export const RuleTypeIABString: string = 'IAB Category';
export const RuleTypeAdomainString: string = 'Adomain';
export const RuleTypeMediafileString: string = 'Mediafile';
export const RuleTypeCreativeIDString: string = 'Creative ID';
export const RuleTypeLanguageString: string = 'Language';
export const RuleTypeEventTrackerString: string = 'Event Tracker';

export function brandSafetyRuleTypeToString(ruleType: number): string {
  switch (ruleType) {
    case RuleTypeIAB:
      return RuleTypeIABString;
    case RuleTypeAdomain:
      return RuleTypeAdomainString;
    case RuleTypeMediafile:
      return RuleTypeMediafileString;
    case RuleTypeCreativeID:
      return RuleTypeCreativeIDString;
    case RuleTypeLanguage:
      return RuleTypeLanguageString;
    case RuleTypeEventTracker:
      return RuleTypeEventTrackerString;
  }
  return RuleTypeIABString;
}

export function brandSafetyRuleTypeToNumber(ruleType: string): number {
  switch (ruleType) {
    case RuleTypeIABString:
      return RuleTypeIAB;
    case RuleTypeAdomainString:
      return RuleTypeAdomain;
    case RuleTypeMediafileString:
      return RuleTypeMediafile;
    case RuleTypeCreativeIDString:
      return RuleTypeCreativeID;
    case RuleTypeLanguageString:
      return RuleTypeLanguage;
    case RuleTypeEventTrackerString:
      return RuleTypeEventTracker;
  }
  return RuleTypeIAB;
}

export function getBrandSafetyTypePills(isRuleTargetingChannel: boolean): IPillItem[] {
  const pills: IPillItem[] = [
    { text: RuleTypeIABString },
    { text: RuleTypeAdomainString },
    { text: RuleTypeMediafileString },
    { text: RuleTypeCreativeIDString },
    { text: RuleTypeEventTrackerString },
  ];

  if (isRuleTargetingChannel) {
    pills.push({ text: RuleTypeLanguageString });
  }

  return pills;
}

// define the rule exception object
export interface IBrandSafetyRuleException {
  // base
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string;

  // specific
  Name: string;
  BrandSafetyRuleID: number; // id of the brandsafetyrule that own the exception
  BidderIDs: string; // the IDs of bidders where the exception is affected joined by ","
  AllowedAdomains: string; // adomains list that's whitelisted understand the rule and the IAB Category
  AllowedIABCategories: string; // defines the IAB Categories, where we will whitelist specific admoains
  TargetedIABExceptions: string; // defines specific IAB Categories exceptions
}

// define the rule object in itself
export interface IBrandSafetyRule {
  // base
  ID: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  DeletedAt?: string;

  // specific
  PublisherID: number; // id of the publisher that own the rule
  Name: string; // rule name
  Active: number; // is rule active - BrandSafetyRuleActive enum
  RuleTargetingType: number; // rule targeting type - BrandSafetyRuleTargetingType
  IsTargetingExcluded: boolean; // is the rule targeting included or excluded for channels, bidders, etc.
  IsTypeTargetingExcluded: boolean; // is what the rule is applying included or excluded (i.e. adomain, languages, IAB-cat)
  NbrResponseBlocked: number; // total number of responses blocked by this rule
  TargetedIABs: string; // if RuleType is IAB → contains targeted IAB list
  TargetedAdomains: string; // if RuleType is Adomain → contains targeted adomain list
  TargetingAdomainExceptionState: BrandSafetyRuleTargetingAdomainExceptionState; // unknown (0), exact match (1), contains (2), allow all (3)
  TargetedMediafiles: string; // if RuleType is Mediafile → contains targeted mediafile list
  TargetedCreativeIDs: string; // if RuleType is CreativeID → contains targeted creative IDs list
  TargetedLanguages: string;
  TargetedEventTrackers: string;
  EnableSaveCreativeValidations: EBool;
  EnableBlockBidsWithEmptyIABCatAdomain: EBool;

  // Targeted Content
  TargetedContentCategory: string;
  TargetedContentCategoryIsAllowed: EAllowStatus;
  TargetedContentChannel: string;
  TargetedContentChannelIsAllowed: EAllowStatus;
  TargetedContentDistributor: string;
  TargetedContentDistributorIsAllowed: EAllowStatus;
  TargetedContentGenre: string;
  TargetedContentGenreIsAllowed: EAllowStatus;
  TargetedContentID: string;
  TargetedContentIDIsAllowed: EAllowStatus;
  TargetedContentNetwork: string;
  TargetedContentNetworkIsAllowed: EAllowStatus;
  TargetedContentProducer: string;
  TargetedContentProducerIsAllowed: EAllowStatus;
  TargetedContentRating: string;
  TargetedContentRatingIsAllowed: EAllowStatus;
  TargetedContentSeries: string;
  TargetedContentSeriesIsAllowed: EAllowStatus;
  TargetedContentTitle: string;
  TargetedContentTitleIsAllowed: EAllowStatus;

  // targeting app/site
  TargetedAppBundleID: string;
  TargetedAppBundleIDIsAllowed: EAllowStatus;
  TargetedSitePages: string;
  TargetedSitePagesIsAllowed: EAllowStatus;

  // Targeted other
  TargetedVenueID: string;
  TargetedVenueIDIsAllowed: EAllowStatus;
  TargetedCoppa: TargetingCoppa;

  // Targeted Custom
  TargetedCustom4: string;
  TargetedCustom4IsAllowed: EAllowStatus;
  TargetedCustom5: string;
  TargetedCustom5IsAllowed: EAllowStatus;
  TargetedCustom6: string;
  TargetedCustom6IsAllowed: EAllowStatus;
  TargetedCustom7: string;
  TargetedCustom7IsAllowed: EAllowStatus;
  TargetedCustom8: string;
  TargetedCustom8IsAllowed: EAllowStatus;
  TargetedCustom9: string;
  TargetedCustom9IsAllowed: EAllowStatus;
  TargetedCustom10: string;
  TargetedCustom10IsAllowed: EAllowStatus;
  TargetedCustom11: string;
  TargetedCustom11IsAllowed: EAllowStatus;
  TargetedCustom12: string;
  TargetedCustom12IsAllowed: EAllowStatus;
  TargetedCustom13: string;
  TargetedCustom13IsAllowed: EAllowStatus;
  TargetedCustom14: string;
  TargetedCustom14IsAllowed: EAllowStatus;
  TargetedCustom15: string;
  TargetedCustom15IsAllowed: EAllowStatus;
  TargetedCustom16: string;
  TargetedCustom16IsAllowed: EAllowStatus;
  TargetedCustom17: string;
  TargetedCustom17IsAllowed: EAllowStatus;
  TargetedCustom18: string;
  TargetedCustom18IsAllowed: EAllowStatus;
  // TODO: add migration for custom targeting in brand safety
  // TargetedCustomTargeting: string;
  // TargetedCustomTargetingIsAllowed: EAllowStatus;

  // Targeted Geo
  TargetingCountryGroupIDs: string;
  TargetingCountryGroupIDsIsAllowed: EAllowStatus;
  TargetingDeviceCountry: string;
  TargetingDeviceCountryIsAllowed: EAllowStatus;
  LocalTargetingAllowed: string;
  LocalTargetingDisallowed: string;
  LocalTargetingAllowedLogic: TargetingLogic;
  LocalTargetingDisallowedLogic: TargetingLogic;

  CountryGroups: ICountryGroup[];

  BrandSafetyRuleExceptions: IBrandSafetyRuleException[]; // block rule exceptions by bidders

  // targeting association table
  TargetedObjects: IBrandSafetyRuleAssociationTable[]; // association table to all IDs targeted by this rule
  TargetingCCPA: TargetingCCPA;
}

// ---------- ASSOCIATION TABLE

export interface IBrandSafetyRuleAssociationTable {
  TargetingType: number;
  TargetID: number;
}

// ---------- HELPER FUNCTIONS

export function targetingTypeToString(targetingType: number) {
  switch (targetingType) {
    case BrandSafetyRuleTargetingTypeBidder:
      return 'bidder';
    case BrandSafetyRuleTargetingTypeBidderGroup:
      return 'bidder group';
    case BrandSafetyRuleTargetingTypeChannel:
      return 'channel';
    case BrandSafetyRuleTargetingTypeChannelGroup:
      return 'channel group';
    default:
      return 'unknown';
  }
}
